import React, { ReactNode } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { WretchError } from 'wretch/resolver';

import { Tracking } from '~/services/tracking';
import { httpCode } from '~/utils/api-fetch/httpCode';

import {
  ErrorMessageWrapper,
  ErrorMessageWrapperProps,
} from '../error-message-wrapper/ErrorMessageWrapper';

export interface ErrorBoundaryWrapperProps {
  children: ReactNode;
  fallback?: ReactNode;
  resetKeys?: number[];
  onErrorCallback?: (error: Error) => void;
}

const isWretchError = (error: unknown): error is WretchError => {
  return (
    error instanceof Error &&
    'status' in error &&
    typeof (error as WretchError).status === 'number' &&
    'response' in error &&
    'url' in error
  );
};

export const ErrorBoundaryWrapper = ({
  children,
  fallback,
  resetKeys,
  onErrorCallback,
  ...rest
}: ErrorBoundaryWrapperProps & ErrorMessageWrapperProps) => {
  const logError = (error: Error) => {
    // Don't log 404 errors
    if (isWretchError(error) && error.status === httpCode.notFound) {
      return;
    }

    Tracking.captureException(error);
  };

  return (
    <ErrorBoundary
      data-testid="error-boundary-wrapper"
      fallback={
        fallback !== undefined ? (
          fallback
        ) : (
          <ErrorMessageWrapper {...rest} displayIcon />
        )
      }
      onError={onErrorCallback ?? logError}
      resetKeys={resetKeys}
    >
      {children}
    </ErrorBoundary>
  );
};
